import React, { useState, useMemo, useEffect } from "react";
import { Button } from "rsuite";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import usePowerBIEmbed from "../../hooks/usePowerBIEmbed";
import usePowerBIButtonNavigation from "../../hooks/usePowerBIButtonNavigation";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import PowerBIEmbedOptimized from "../power-bi/PowerBIEmbedOptimized";
import HTLoader from "../common/ht-loader/HTLoader";
import DateRange from "../dateRange/DateRange";
import { getPowerBIDateRangeFilter } from "../../constants/utils";
import { PowerBINavigationProvider } from "../../contexts/PowerBINavigationContext";
import "./PerformanceInsights.scss";
import "../power-bi/report.scss";

const REPORT_CLASS = "report-container";
const DEBOUNCE_DELAY = 500; // 500ms debounce delay for filter updates

const PerformanceInsightsContent = () => {
  const navigate = useNavigate();

  // Initialize with last 30 days
  const defaultDateRange = useMemo(() => {
    const endDate = dayjs().toDate();
    const startDate = dayjs().subtract(365, "day").toDate();
    return [startDate, endDate];
  }, []);

  const [dateRange, setDateRange] = useState(defaultDateRange);

  const handleAnalyticsHubClick = () => {
    navigate("/analytics-hub/summary");
  };

  // Get button navigation handlers (pass null initially, will update with report instance below)
  const buttonHandlers = usePowerBIButtonNavigation(null);

  const {
    report,
    bootstrapConfig,
    reportConfig,
    isBootstrapped,
    isEmbedded,
    isLoaded,
    eventHandlersMap,
    handleEmbedComponent,
    applyFilter,
  } = usePowerBIEmbed(
    "Performance Insights",
    buttonHandlers, // Pass button handlers as additionalEventHandlers
    "Supplier Portal Home Page",
    "Performance Insights"
  );

  // Update button navigation hook with report instance once it's available
  usePowerBIButtonNavigation(report);

  // Reset to default date range when dateRange is cleared/empty
  useEffect(() => {
    if (!dateRange || dateRange.length === 0) {
      setDateRange(defaultDateRange);
    }
  }, [dateRange, defaultDateRange]);

  // Apply date range filter when dateRange changes with debounce and cleanup
  useEffect(() => {
    if (!report || !isLoaded || !dateRange || dateRange?.length !== 2) {
      return;
    }

    // Debounce filter updates to prevent race conditions on rapid changes
    const timeoutId = setTimeout(() => {
      const [startDate, endDate] = dateRange;

      try {
        const dateFilter = getPowerBIDateRangeFilter(startDate, endDate);
        applyFilter(dateFilter);
      } catch (error) {
        getLogger().error("PerformanceInsights", {
          type: "Error",
          page_url: window.location.href,
          error_desc: "Error applying date filter",
          error_stack: error?.stack ?? null,
        });
        // Reset to default date range if invalid dates are provided
        setDateRange(defaultDateRange);
      }
    }, DEBOUNCE_DELAY);

    // Cleanup: cancel pending filter update if dateRange changes again
    return () => clearTimeout(timeoutId);
  }, [dateRange, report, isLoaded, applyFilter, defaultDateRange]);

  return (
    <section className="performance-insights">
      <div className="performance-insights-header">
        <div className="performance-insights-header-left">
          <h2>Performance Insights</h2>
          <span className="performance-insights-subtitle">
            Based on Arrival Dates
          </span>
        </div>
        <div className="performance-insights-header-right">
          <DateRange
            value={dateRange}
            setValue={setDateRange}
            label="Arrival Date Range"
            allowPastDates={true}
          />
          <Button
            className="performance-section-button"
            onClick={handleAnalyticsHubClick}
          >
            Analytics Hub &gt;
          </Button>
        </div>
      </div>
      <div
        className="performance-insights-iframe-container"
        data-testid="performance-insights-iframe-container"
      >
        {!isLoaded && <HTLoader loaderText="" />}
        <PowerBIEmbedOptimized
          bootstrapConfig={bootstrapConfig}
          embedConfig={reportConfig}
          isBootstrapped={isBootstrapped}
          isEmbedded={isEmbedded}
          cssClassName={REPORT_CLASS + (isLoaded ? "" : " invisible")}
          getEmbeddedComponent={handleEmbedComponent}
          eventHandlers={eventHandlersMap}
          uniqueId="performance-insights"
        />
      </div>
    </section>
  );
};

// Wrap with PowerBINavigationProvider to enable navigation context
const PerformanceInsights = () => {
  return (
    <PowerBINavigationProvider>
      <PerformanceInsightsContent />
    </PowerBINavigationProvider>
  );
};

export default PerformanceInsights;
